import { morph } from '@theme/morph';

const SWAP_SECTION_ID = 'color-swatch-card';

/**
 * `<color-swatches>` swaps a product card in place when a color swatch is clicked.
 *
 * Colors are modelled as SEPARATE products linked through the
 * `custom.productos_relacionados` metafield. Clicking a swatch re-renders the
 * target product through the `color-swatch-card` section (a full card fragment,
 * including the complete gallery) and patches the gallery, title, price, badge,
 * sku and links onto the live card — reusing the theme's section-rendering + morph machinery.
 *
 * The target fragment is prefetched on hover so the click swap is instant.
 */
class ColorSwatches extends HTMLElement {
  /** @type {Map<string, Promise<Document>>} */
  #cache = new Map();
  #prefetched = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePrefetch);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePrefetch);
  }

  /**
   * Warms the cache for every swatch the first time the row is hovered, so any
   * subsequent click swap is instant (including swapping back to the initial color).
   */
  #handlePrefetch = () => {
    if (this.#prefetched) return;
    this.#prefetched = true;

    for (const button of this.querySelectorAll('[data-swatch]')) {
      const url = button.getAttribute('data-url');
      if (url) this.#fetchCard(url).catch(() => {});
    }
  };

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-swatch]') : null;
    if (!button) return;

    // Swatches live inside the card link; never let the click navigate the card.
    event.preventDefault();
    event.stopPropagation();

    if (button.getAttribute('aria-pressed') === 'true') return;

    const url = button.getAttribute('data-url');
    if (url) this.#swap(url, button);
  };

  /**
   * Fetches (and caches) the target product's card fragment.
   * @param {string} productUrl - The target product URL.
   * @returns {Promise<Document>}
   */
  #fetchCard(productUrl) {
    const key = productUrl.split('?')[0];
    const cached = this.#cache.get(key);
    if (cached) return cached;

    const request = fetch(`${key}?section_id=${SWAP_SECTION_ID}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        // Warm the HTTP image cache so the swapped image is already downloaded on click.
        this.#preloadImages(doc);
        return doc;
      })
      .catch((error) => {
        // Don't poison the cache with a rejected request.
        this.#cache.delete(key);
        throw error;
      });

    this.#cache.set(key, request);
    return request;
  }

  /**
   * Fetches the target product's card fragment and patches it onto the live card.
   * @param {string} productUrl - The target product URL.
   * @param {Element} swatchButton - The clicked swatch button.
   */
  async #swap(productUrl, swatchButton) {
    const card = this.closest('product-card');
    if (!card) return;

    this.setAttribute('aria-busy', 'true');

    try {
      const doc = await this.#fetchCard(productUrl);
      const source = doc.querySelector('product-card');
      if (!source) throw new Error('No product-card in response');

      this.#applyGallery(card, source);
      this.#applySizes(card, source);
      this.#applyPrice(card, source);
      this.#applySku(card, source);
      this.#applyLinks(card, source);
      this.#applyTitle(card, source);

      const productId = source.getAttribute('data-product-id');
      if (productId) card.setAttribute('data-product-id', productId);

      this.#setActive(swatchButton);
      this.removeAttribute('aria-busy');

      // Show a skeleton over the gallery until the swapped image actually finishes loading.
      this.#settleGalleryImage(card);
    } catch (error) {
      // Match the theme convention (product-card.js) of warning, not throwing.
      console.warn('[color-swatches] swap failed:', error);
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Preloads the first images of a fetched fragment so they are cached before a click.
   * @param {Document} doc
   */
  #preloadImages(doc) {
    const images = doc.querySelectorAll("[ref='cardGallery'] slideshow-slide img");
    let count = 0;
    for (const img of images) {
      if (count >= 2) break; // featured + hover image
      const preload = new Image();
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (sizes) preload.sizes = sizes;
      if (srcset) preload.srcset = srcset;
      else if (src) preload.src = src;
      count += 1;
    }
  }

  /**
   * Toggles a shimmer skeleton on the gallery until the swapped image has loaded.
   * Skips the skeleton entirely when the image is already cached.
   * @param {Element} card
   */
  async #settleGalleryImage(card) {
    const gallery = card.querySelector("[ref='cardGallery']");
    if (!gallery) return;

    const image =
      gallery.querySelector("slideshow-slide[aria-hidden='false'] img") ?? gallery.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;
    if (image.complete && image.naturalWidth > 0) return;

    gallery.classList.add('is-color-swapping');
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        resolve(undefined);
      };
      image.addEventListener('load', finish);
      image.addEventListener('error', finish);
      setTimeout(finish, 3000);
    });
    gallery.classList.remove('is-color-swapping');
  }

  /**
   * Replaces the full gallery (all media: featured, hover, carousel) and the badge,
   * keeping the live gallery wrapper so its aspect ratio and layout are preserved.
   * @param {Element} card
   * @param {Element} source
   */
  #applyGallery(card, source) {
    const liveGallery = card.querySelector("[ref='cardGallery']");
    const sourceGallery = source.querySelector("[ref='cardGallery']");
    if (!liveGallery || !sourceGallery) {
      this.#applyFeaturedImageFallback(card, source);
      this.#applyBadges(liveGallery, sourceGallery);
      return;
    }

    const liveSlideshow = liveGallery.querySelector('slideshow-component');
    const sourceSlideshow = sourceGallery.querySelector('slideshow-component');
    if (liveSlideshow && sourceSlideshow) {
      // Insert a fresh node so the slideshow custom element re-initializes cleanly.
      liveSlideshow.replaceWith(document.importNode(sourceSlideshow, true));
    } else {
      this.#applyFeaturedImageFallback(card, source);
    }

    const galleryHref = sourceGallery.querySelector("a[ref='cardGalleryLink']")?.getAttribute('href');
    const liveGalleryLink = liveGallery.querySelector("[ref='cardGalleryLink']");
    if (galleryHref && liveGalleryLink instanceof HTMLAnchorElement) {
      liveGalleryLink.href = galleryHref;
    }

    this.#applyBadges(liveGallery, sourceGallery);
  }

  /**
   * Replaces the size overlay with the target color's sizes. The color-swatches
   * block (and thus the .has-size-* classes on the card) is not re-rendered on
   * swap, so the new overlay inherits the same interactivity via those classes.
   * @param {Element} card
   * @param {Element} source
   */
  #applySizes(card, source) {
    const gallery = card.querySelector("[ref='cardGallery']");
    if (!gallery) return;

    const target = gallery.querySelector('.card-sizes');
    const next = source.querySelector("[ref='cardGallery'] .card-sizes");

    if (target && next) {
      target.replaceWith(document.importNode(next, true));
    } else if (target && !next) {
      target.remove();
    } else if (!target && next) {
      gallery.appendChild(document.importNode(next, true));
    }
  }

  /**
   * Fallback: swap only the visible image's srcset when the gallery structure can't be replaced.
   * @param {Element} card
   * @param {Element} source
   */
  #applyFeaturedImageFallback(card, source) {
    const mediaUrl = source.getAttribute('data-featured-media-url');
    if (!mediaUrl) return;

    const gallery = card.querySelector("[ref='cardGallery']");
    const image =
      gallery?.querySelector("slideshow-slide[aria-hidden='false'] img") ?? gallery?.querySelector('img');
    if (image instanceof HTMLImageElement) {
      image.srcset = mediaUrl;
      image.src = mediaUrl;
    }
  }

  /**
   * @param {Element | null} liveGallery
   * @param {Element | null} sourceGallery
   */
  #applyBadges(liveGallery, sourceGallery) {
    const target = liveGallery?.querySelector('.product-badges');
    const next = sourceGallery?.querySelector('.product-badges');
    if (target && next) morph(target, next);
  }

  /**
   * @param {Element} card
   * @param {Element} source
   */
  #applyPrice(card, source) {
    const target = card.querySelector("product-price [ref='priceContainer']");
    const next = source.querySelector("product-price [ref='priceContainer']");
    if (target && next) morph(target, next);
  }

  /**
   * @param {Element} card
   * @param {Element} source
   */
  #applySku(card, source) {
    const target = card.querySelector('product-sku-component');
    const next = source.querySelector('product-sku-component');
    if (target && next) morph(target, next);
  }

  /**
   * @param {Element} card
   * @param {Element} source
   */
  #applyLinks(card, source) {
    const sourceLink = source.querySelector("a[ref='productCardLink']") ?? source.querySelector('a[href]');
    const href = sourceLink?.getAttribute('href');
    if (!href) return;

    for (const selector of ['.product-card__link', "[ref='cardGalleryLink']", "[ref='productTitleLink']"]) {
      const el = card.querySelector(selector);
      if (el instanceof HTMLAnchorElement) el.href = href;
    }
  }

  /**
   * @param {Element} card
   * @param {Element} source
   */
  #applyTitle(card, source) {
    const title = source.getAttribute('data-product-title');
    if (!title) return;

    const titleLink = card.querySelector("[ref='productTitleLink']");
    const heading = titleLink?.querySelector('[role="heading"]') ?? titleLink;
    if (heading) heading.textContent = title;
  }

  /**
   * @param {Element} active - The swatch button that is now selected.
   */
  #setActive(active) {
    for (const button of this.querySelectorAll('[data-swatch]')) {
      const isActive = button === active;
      button.classList.toggle('color-swatch--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    const handle = active.getAttribute('data-handle');
    if (handle) this.setAttribute('data-current-handle', handle);
  }
}

if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}
